import { useCallback, useEffect, useMemo, useState } from "react";

import { MattersAPI } from "../../../api/matters";
import type { MatterDTO } from "../../../api/types/matter";

const createdAtMs = (m: MatterDTO): number => {
  const ms = Date.parse((m.case?.created_at ?? "").toString());
  return Number.isFinite(ms) ? ms : 0;
};

export type ClientMatters = {
  matters: MatterDTO[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Most recently created case, if any. */
  newestMatter: MatterDTO | null;
  /**
   * Insert a just-created case, optionally swapping out the one it replaces
   * (onboarding's "change case type" creates a new case and deletes the old).
   */
  applyCreated: (created: MatterDTO, replaced?: MatterDTO | null) => void;
};

/** The client's cases (matters), loaded once per client and refreshable. */
export const useClientMatters = (clientId: string | null): ClientMatters => {
  const [matters, setMatters] = useState<MatterDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await MattersAPI.listForForeignNational(clientId);
      setMatters(Array.isArray(response) ? response : []);
    } catch (e) {
      setMatters([]);
      setError(
        e instanceof Error
          ? e.message
          : "Failed to load cases. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    setMatters([]);
    setError(null);
    refresh();
  }, [refresh]);

  const newestMatter = useMemo(() => {
    if (matters.length === 0) return null;
    return (
      [...matters].sort((a, b) => createdAtMs(b) - createdAtMs(a))[0] ?? null
    );
  }, [matters]);

  const applyCreated = useCallback(
    (created: MatterDTO, replaced?: MatterDTO | null) => {
      setMatters((prev) => {
        const rest = replaced
          ? prev.filter((m) => m.individual_id !== replaced.individual_id)
          : prev;
        return [created, ...rest];
      });
      if (replaced?.individual_id) {
        MattersAPI.delete(replaced.individual_id).catch(() => {
          // Best-effort cleanup after the swap; the UI already shows the new case.
        });
      }
    },
    []
  );

  return { matters, isLoading, error, refresh, newestMatter, applyCreated };
};
